/**
 * Screening V2 Types
 *
 * Types for the streamlined 3-screen qualification flow.
 * - Screen 1: Qualification (yes/no/unknown questions)
 * - Screen 2: Data Collection (click-to-mark fields)
 * - Screen 3: AI Review (transcript extraction + human review)
 */

// ---------------------------------------------------------------------------
// Answer & Outcome Types
// ---------------------------------------------------------------------------

export type QualificationAnswer = 'yes' | 'no' | 'unknown' | undefined;

export type QualificationOutcome = 'qualified' | 'not_qualified' | 'pending';

export type TranscriptStatus =
  | 'waiting'
  | 'processing'
  | 'complete'
  | 'no_call_found'
  | 'failed';

export type AIFieldStatus = 'extracted' | 'confirmed' | 'edited' | 'rejected';

// ---------------------------------------------------------------------------
// Screen 1: Qualification Types
// ---------------------------------------------------------------------------

export interface QualificationQuestion {
  id: string;
  /** The yes/no/unknown question text */
  text: string;
  /** Follow-up prompt to ask the client for context */
  contextPrompt: string;
  /** If true, wrong answer = don't sign */
  isDisqualifier: boolean;
  /** Expected answer for qualification (true = yes, false = no) */
  expectedAnswer: boolean;
  /** Display order for guiding intaker through questions (1-based, reading order: left-right, top-bottom) */
  order?: number;
  /** If true, note/reason is not required for qualification */
  noteOptional?: boolean;
  /** If set, the note syncs to this form_data path (e.g., 'incident_info.present_complaints') */
  noteFormDataPath?: string;
}

export interface QualificationState {
  answers: Record<string, QualificationAnswer>;
  contextAsked: Record<string, boolean>;
  notes: Record<string, string>;
}

// ---------------------------------------------------------------------------
// Screen 2: Data Collection Types
// ---------------------------------------------------------------------------

export type DataCollectionPriority = 'P1' | 'P2';

export type DataCollectionInputType =
  | 'yes_no'
  | 'yes_no_unknown'
  | 'text'
  | 'date'
  | 'ask_client'
  | 'group' // Group is a container with only sub-questions, no direct answer
  | 'object_array'; // Array of objects (e.g., multiple providers)

export type YesNoAnswer = 'yes' | 'no' | undefined;
export type YesNoUnknownAnswer = 'yes' | 'no' | 'unknown' | undefined;

export interface DataCollectionAnswer {
  /** The value entered (string for text/date, 'yes'/'no'/'unknown' for choices) */
  value: string | undefined;
  /** Whether the intaker has marked this as "asked" (for ask_client type) */
  asked: boolean;
  /** For object_array type: array of objects */
  arrayValue?: Record<string, string>[];
}

// ---------------------------------------------------------------------------
// Object Array Field Types (for object_array input type)
// ---------------------------------------------------------------------------

export interface ObjectArrayFieldSchema {
  /** Field key (used in the object) */
  key: string;
  /** Display label */
  label: string;
  /** Input type for this field */
  type: 'text' | 'date' | 'textarea';
  /** Placeholder text */
  placeholder?: string;
  /** Whether this field is required */
  required?: boolean;
}

export interface ObjectArrayConfig {
  /** Schema defining the fields for each object */
  fields: ObjectArrayFieldSchema[];
  /** Button text for adding new item */
  addButtonText?: string;
  /** How to display each item in the compact list (uses field keys) */
  displayFormat?: string; // e.g., "{name} · {type}"
}

/**
 * Condition for showing/hiding a sub-question.
 * Supports two modes:
 * 1. parentAnswer: Check the main question's answer (legacy, simple)
 * 2. questionId + answer: Check any question or sub-question's answer by ID (nested conditions)
 */
export type SubQuestionShowCondition =
  | { parentAnswer: 'yes' | 'no' | 'unknown' | 'any' } // 'any' means always show if parent is answered
  | { questionId: string; answer: 'yes' | 'no' | 'unknown' }; // Check specific question/sub-question by ID

export interface DataCollectionSubQuestion {
  id: string;
  /** The question text or label */
  text: string;
  /** Input type for this sub-question */
  inputType: Exclude<DataCollectionInputType, 'group'>;
  /** Conditional display based on another question's answer */
  showCondition?: SubQuestionShowCondition;
  /** For ask_client: the prompt to show */
  askClientPrompt?: string;
  /** For text inputs: placeholder text */
  placeholder?: string;
  /** Whether this field is required */
  required?: boolean;
  /**
   * Direct-write path into form_data (e.g., "personal_info.dob").
   * When set, answers write directly to `form_data.*` instead of going through
   * the sync layer.
   */
  formDataPath?: string;
  /** Helper text shown below the input as a hint */
  helperText?: string;
}

export interface DataCollectionQuestion {
  id: string;
  /** The main question text */
  text: string;
  /** Input type for the main question */
  inputType: DataCollectionInputType;
  /** Priority level (P1 = must collect, P2 = nice to have) */
  priority: DataCollectionPriority;
  /** For ask_client: the prompt to show */
  askClientPrompt?: string;
  /** For text inputs: placeholder text */
  placeholder?: string;
  /** Sub-questions (always shown or conditional) */
  subQuestions?: DataCollectionSubQuestion[];
  /** Section label for grouping (e.g., "Insurance Information") */
  sectionLabel?: string;
  /** For object_array: configuration for the array fields */
  objectArrayConfig?: ObjectArrayConfig;
  /** Explicit column assignment (overrides index-based alternating) */
  column?: 'left' | 'right';
  /**
   * Direct-write path into form_data (e.g., "incident_info.police_report").
   * When set, answers write directly to `form_data.*` instead of going through
   * the sync layer (`syncV2DataCollection.ts`).
   */
  formDataPath?: string;
  /** Helper text shown below the input as a hint */
  helperText?: string;
}

// Legacy type for backwards compatibility
export type DataFieldCategory =
  | 'INCIDENT_DETAILS'
  | 'INSURANCE_INFO'
  | 'MEDICAL_TREATMENT'
  | 'PERSONAL_INFO'
  | 'DEFENDANT_INFO';

export interface DataCollectionField {
  id: string;
  /** Short label: "Insurance Company" */
  label: string;
  /** What to ask: "Who is your insurance provider?" */
  description: string;
  /** Grouping for grid layout */
  category: DataFieldCategory;
}

// ---------------------------------------------------------------------------
// Screen 3: AI Review Types
// ---------------------------------------------------------------------------

export interface AIExtractedField {
  value: string;
  confidence: number;
  sourceQuote: string | null;
  status: AIFieldStatus;
}

// ---------------------------------------------------------------------------
// Main V2 Data Structure (mirrors backend ScreeningV2DataSchema)
// ---------------------------------------------------------------------------

export interface ScreeningV2Data {
  version: 'v2';
  currentScreen: 1 | 2 | 3 | 4;

  // Screen 1: Qualification
  qualificationAnswers: Record<string, QualificationAnswer>;
  qualificationContextAsked: Record<string, boolean>;
  qualificationNotes: Record<string, string>;
  qualificationOutcome: QualificationOutcome;
  qualificationCompletedAt: string | null;

  // Actions taken
  retainerSentAt: string | null;
  rejectedAt: string | null;
  escalatedAt: string | null;

  // Screen 2: Data Collection
  dataCollectionAnswers: Record<string, DataCollectionAnswer>;
  collectedFields: string[]; // Legacy - IDs of completed questions
  collectionCompletedAt: string | null;

  // Screen 3: AI Review
  transcriptStatus: TranscriptStatus;
  matchedCallId: string | null;
  aiExtractedFields: Record<string, AIExtractedField>;
  humanReviewedFields: Record<string, string>;
  reviewCompletedAt: string | null;

  // Metadata
  intakerId: string | null;
  intakerName: string;

  // End Intake
  firstContactOutcome: FirstContactOutcome | null;
  endIntakeClickedAt: string | null;
}

// ---------------------------------------------------------------------------
// Component Props Types
// ---------------------------------------------------------------------------

export interface QualificationScreenProps {
  questions: QualificationQuestion[];
  answers: Record<string, QualificationAnswer>;
  contextAsked: Record<string, boolean>;
  notes: Record<string, string>;
  outcome: QualificationOutcome;
  incidentDate: string;
  incidentState: string;
  /** Whether retainer has already been sent */
  retainerSent: boolean;
  onAnswerChange: (questionId: string, answer: QualificationAnswer) => void;
  onContextAsked: (questionId: string) => void;
  onNoteChange: (questionId: string, note: string) => void;
  onResetQuestion: (questionId: string) => void;
  onIncidentDateChange: (date: string) => void;
  onIncidentStateChange: (state: string) => void;
  /** Trigger CMS send flow (opens LOE dialog) */
  onSendRetainer: () => void;
  /** Mark retainer as sent manually (outside CMS) */
  onMarkRetainerSentManually: () => void;
  onReject: (reason: string) => void;
  isRetainerSending: boolean;
  /** Whether to show the "Continue to Collect" prompt (true when qualified and has collect questions) */
  showCollectPrompt?: boolean;
  /** Callback to navigate to the collect screen */
  onNavigateToCollect?: () => void;
}

export interface DataCollectionScreenProps {
  questions: DataCollectionQuestion[];
  answers: Record<string, DataCollectionAnswer>;
  onAnswerChange: (questionId: string, value: string | undefined) => void;
  onAskedChange: (questionId: string, asked: boolean) => void;
  onFinish: () => void;
}

export interface AIReviewScreenProps {
  transcriptStatus: TranscriptStatus;
  extractedFields: Record<string, AIExtractedField>;
  reviewedFields: Record<string, string>;
  onRefreshTranscript: () => void;
  onEnterManually: () => void;
  onFieldConfirm: (fieldId: string) => void;
  onFieldEdit: (fieldId: string, value: string) => void;
  onComplete: () => void;
}

export interface ScreeningV2SidebarProps {
  currentScreen: 1 | 2 | 3 | 4;
  screen1Status: 'pending' | 'complete' | 'blocked';
  screen2Status: 'locked' | 'pending' | 'complete';
  screen3Status: 'locked' | 'pending' | 'complete';
  screen4Status: 'locked' | 'pending' | 'complete';
  onNavigate: (screen: 1 | 2 | 3 | 4) => void;
}

// ---------------------------------------------------------------------------
// Default/Initial State
// ---------------------------------------------------------------------------

export const createInitialScreeningV2Data = (
  intakerId: string | null = null,
  intakerName = ''
): ScreeningV2Data => ({
  version: 'v2',
  currentScreen: 1,
  qualificationAnswers: {},
  qualificationContextAsked: {},
  qualificationNotes: {},
  qualificationOutcome: 'pending',
  qualificationCompletedAt: null,
  retainerSentAt: null,
  rejectedAt: null,
  escalatedAt: null,
  dataCollectionAnswers: {},
  collectedFields: [],
  collectionCompletedAt: null,
  transcriptStatus: 'waiting',
  matchedCallId: null,
  aiExtractedFields: {},
  humanReviewedFields: {},
  reviewCompletedAt: null,
  intakerId,
  intakerName,
  firstContactOutcome: null,
  endIntakeClickedAt: null,
});

// ---------------------------------------------------------------------------
// End Intake Types
// ---------------------------------------------------------------------------

export type FirstContactOutcome = 'completed' | 'incomplete' | 'unavailable';

export interface MissingField {
  screen: 'Header' | 'Screen 1 (Qualification)' | 'Screen 2 (Data Collection)';
  fieldId: string;
  label: string;
}

export interface EndIntakeValidationResult {
  isValid: boolean;
  missingFields: MissingField[];
}

export interface EndIntakePayload {
  outcome: FirstContactOutcome;
  missing_fields: Array<{
    screen: string;
    field_id: string;
    label: string;
  }>;
  screening_link: string;
  client_name?: string;
  firm_name?: string;
  retainer_status?: 'sent' | 'not_sent';
  rejection_reason?: string;
  unavailable_reason?: string;
  intaker_email?: string;
  intaker_name?: string;
}
